import React, { useEffect, useReducer, useRef } from 'react';
import axios from 'axios';
import {
  Button, ButtonToolbar, Form, FormInstance, InputNumber, Loader, Message,
} from 'rsuite';
import handleError from '../../../../api/apiErrorHandle';
import { showSuccessMessage } from '../../../../utils/message';
import { loadMargins, saveMargins } from './pricingMarginsService';
import { Edit, initialState, reducer } from './pricingMarginsState';
import validationModel from './pricingMarginsValidation';

const SAVED_MESSAGE = 'Margins saved. They are applied to pricing data after a recalculation.';
const SAVED_MESSAGE_DURATION = 5000;

interface Props {
  marketplaceCode: string;
  partnerProductCode: string;
  onCancel: () => void;
  onSaved: () => void;
}

export default function PricingMarginsEdit({
  marketplaceCode,
  partnerProductCode,
  onCancel,
  onSaved,
}: Props): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const formRef = useRef<FormInstance>(null);

  async function fetchMargins(signal: AbortSignal) {
    dispatch({ type: 'status/set', payload: 'loading' });

    try {
      const edit = await loadMargins(marketplaceCode, partnerProductCode, signal);
      dispatch({ type: 'edit/loaded', payload: edit });
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      handleError(error);
      onCancel();
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    fetchMargins(controller.signal).then();

    return () => {
      controller.abort();
    };
  }, [marketplaceCode, partnerProductCode]);

  async function submitMargins() {
    dispatch({ type: 'status/set', payload: 'saving' });

    try {
      await saveMargins(marketplaceCode, partnerProductCode, state.edit);
      showSuccessMessage(SAVED_MESSAGE, SAVED_MESSAGE_DURATION);
      onSaved();
    } catch (error) {
      handleError(error);
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  function handleSave() {
    if (!formRef.current?.check()) {
      return;
    }

    submitMargins().then();
  }

  if (state.status === 'loading') {
    return <Loader content="Loading margins..." />;
  }

  return (
    <Form
      ref={formRef}
      fluid
      formValue={state.edit}
      onChange={(edit) => dispatch({ type: 'edit/set', payload: edit as Edit })}
      model={validationModel}
      layout="vertical"
    >
      <Message type="info" style={{ marginBottom: 10 }}>
        Margins are stored on the imported pricing product. The grid keeps showing
        the last calculation until the product is recalculated.
      </Message>

      <Form.Group>
        <Form.ControlLabel>Partner Min Margin w.o. VAT</Form.ControlLabel>
        <Form.Control name="partnerDemandedMinimumMarginAbs" accepter={InputNumber} />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Partner Min Margin (%)</Form.ControlLabel>
        <Form.Control name="partnerRelativeDemandedMinimumMargin" accepter={InputNumber} />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Partner Regular Margin w.o. VAT</Form.ControlLabel>
        <Form.Control name="partnerRegularMarginAbs" accepter={InputNumber} />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Partner Regular Margin (%)</Form.ControlLabel>
        <Form.Control name="partnerRelativeRegularMargin" accepter={InputNumber} />
      </Form.Group>

      <ButtonToolbar>
        <Button
          appearance="primary"
          onClick={() => handleSave()}
          loading={state.status === 'saving'}
          type="submit"
        >
          Save
        </Button>
        <Button
          appearance="ghost"
          onClick={() => onCancel()}
          disabled={state.status === 'saving'}
        >
          Cancel
        </Button>
      </ButtonToolbar>
    </Form>
  );
}
